import {
  deserify,
  getDifficultyLevelString,
  isCorrectAnswer,
  IStyles,
  Loader,
  pxToRem,
  Question as QuestionContainer,
  toStringBloomLevel,
  useCommonServiceClientContext,
  useLoadingTimer,
  useSecondsTimer,
} from '@geneo2-web/shared-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  endModuleSessionApi,
  fetchInternalQuestionIdApi,
  fetchQuestionMetadataApi,
  fetchQuestionsMetadataBatchApi,
  getConceptMasteryApi,
  getMyPerformanceApi,
  getRecommendationsQuestionsAPI,
  IConceptMasteryResponse,
  IPerformanceSummary,
  IQuestionMetadataBatchResponse,
  IQuestionMetadataIdentifiedLearningObjective,
  IQuestionMetadataResponse,
  IQuestionMetadataSolutionStep,
  RecommendationsQuestionsResponse,
  submitAnswerApi,
  type ISubmitAnswerResponse,
  type PalNavigationDection,
} from '../api/learners';
import {
  McqMultipleContentModel,
  McqSingleContentModel,
  Question,
  Question_QuestionEnum,
  QuestionContentModel,
  TfContentModel,
  TfContentModel_AnswerEnum,
} from '@protos/content_management/content.db_pb';
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  PalAssessmentWrapper,
  PalMode,
  difficultyColors,
  getPalModeFromPathName,
  palModes,
  questionTypeTitleFromQuestionContent,
} from '../components/PalAssessmentWrapper';
import {
  startDiagnosticApi,
  submitDiagnosticAnswerApi,
  applyDiagnosticToPalApi,
  getDiagnosticResultsApi,
} from '../api/diagnostic';
import {
  useAppDispatch,
  useAppSelector,
} from 'apps/student/src/reduxStore/reduxHooks';
import {
  HOME,
  PAL_CHALLENGE,
  PAL_DIAGNOSTIC,
  PAL_KNOWLEDGE_CONCEPTS,
  PAL_PRACTICE,
} from 'apps/student/src/routeHandling/RoutesNomenclature';
import { handleKnowledgeConceptsNavigation } from '../KnowledgeConcepts/knowledgeConceptsNavigationUtil';
import { PalModeChangePopup } from '../components/PalModeChangePopup';
import {
  getChallengeApi,
  getChallengeResultsApi,
  IChallengeAnswerResponse,
  submitChallengeAnswerApi,
} from '../api/challenge';
import { Learnometer } from '../components/Learnometer';
import { usePalBackNavigationHandlers } from '../hooks/usePalBackNavigationHandlers';
import { usePalModePopup } from '../hooks/usePalModePopup';
import { PalAnswerOverlay } from '../components/PalAnswerOverlay';
import { HintPopupPal } from '../components/HintPopupPal';
import { QAttemptResultEnum } from '@protos/learning_management/lms.db_pb';
import { LearningPath } from '../components/LearningPath';
import { componentsConfigFn } from './palQuestionConfig';
import { AnswerHintUsedEnum, AnswerModeEnum, Score } from '../api/common';
import { setQuestionCount, setStreak } from '../reducer/pal.slice';
import { getKnowledgeConceptGraph, getModuleMasteryApi } from '../api/module';
import { ConceptNode } from '../KnowledgeConcepts/knowledgeConcepts.types';

import { PerformancePopup, ReviewResult } from '../components/PerformancePopup';
import { useGlobalContext } from 'apps/student/src/app/Context/GlobalContextProvider';
import { use } from 'video.js/dist/types/tech/middleware';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import { PerformanceDrawer } from '../components/PerformanceDrawer';
import { performanceDrawerDummyData } from '../components/PerformanceDrawer/performanceDrawerData';
import PalLoader from '../components/PalLoader';
import { EndSessionDrawer } from '../components/EndSessionDrawer';

const drawerStyles: IStyles = {
  endTestBtn: {
    backgroundColor: '#fff',
    color: '#ef4444',
    border: '1px solid #ef4444',
    // px: pxToRem(14),
    // py: pxToRem(10),
    px: 5,
    py: 1.25,
    width: 'fit-content',
    borderRadius: pxToRem(8),
    fontWeight: 600,
    fontSize: pxToRem(13),
    textTransform: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#ef4444',
      color: '#fff',
      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
    },
  },
};
const PalQuestion: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const palMode = getPalModeFromPathName(location.pathname);
  const componentConfig = componentsConfigFn(palMode);
  const currentPalMode =
    palModes.find((mode) => mode.key === palMode) || palModes[0];
  const currentPalModeKey = currentPalMode.key;
  const isDiagnostic = currentPalModeKey == 'warmup';
  const isPractice = currentPalModeKey == 'practice';
  const isChallenge = currentPalModeKey == 'challenge';
  const streak = useAppSelector((state) => state.pal.streak);
  const question_count = useAppSelector((state) => state.pal.question_count);
  const [diagnosticResult, setDiagnosticResult] = useState<
    ReviewResult | undefined
  >(undefined);
  const [overallChallengeRes, setOverallChallengeResult] = useState<
    ReviewResult | undefined
  >(undefined);
  const dispatch = useAppDispatch();

  const {
    external_module_id,
    external_session_id,
    external_concept_id,
    external_question_id,
  } = useParams();

  const currentStreak = useMemo(() => {
    if (streak?.session_id !== external_session_id) return 0;
    if (streak?.concept_id !== external_concept_id) return 0;
    if (palMode == 'practice') {
      return streak?.practice || 0;
    } else if (palMode == 'challenge') {
      return streak?.challenge || 0;
    } else {
      return 0;
    }
  }, [
    palMode,
    JSON.stringify(streak),
    external_session_id,
    external_concept_id,
  ]);

  const currentQuestionsAnswered = useMemo(() => {
    if (question_count?.session_id !== external_session_id) return 0;
    if (question_count?.concept_id !== external_concept_id) return 0;
    if (palMode == 'practice') {
      return question_count?.practice || 0;
    } else if (palMode == 'challenge') {
      return question_count?.challenge || 0;
    } else if (palMode == 'warmup') {
      return question_count?.warmup || 0;
    } else {
      return 0;
    }
  }, [
    palMode,
    JSON.stringify(streak),
    external_session_id,
    external_concept_id,
  ]);

  const { showPalModePopup, onPalModePopupOpenChange } = usePalModePopup({
    currentPalModeKey,
    uniqueId: external_concept_id,
    shouldCompareUniqueId: !isDiagnostic,
  });

  const [question, setQuestion] = useState<Question | undefined>(undefined);
  const [questionMetadata, setQuestionMetadata] = useState<
    IQuestionMetadataResponse | undefined
  >(undefined);
  const [submittedTime, setSubmittedTime] = useState(0);

  const questionTimerInSeconds =
    questionMetadata?.context?.ideal_time_seconds || 120;
  const [isFirstQuestion, setIsFirstQuestion] = useState(true);

  const timer = useSecondsTimer({
    initialTime: 0,
    isPaused: showPalModePopup,
  });

  const remainingTimeInSeconds = useMemo(() => {
    return questionTimerInSeconds - timer.value;
  }, [timer.value, questionTimerInSeconds]);
  const [loading, setLoading] = useState<
    | 'loading'
    | 'completed'
    | 'error'
    | 'error-submit'
    | 'error-session'
    | 'loading-submit'
    | 'loading-overlay-continue'
    | 'error-overlay-continue'
  >('loading');
  const [isLoaderLoaded, setIsLoaderLoaded] = useState(false);
  const [userAttemptedAnswer, setUserAttemptedAnswer] = useState<string[]>([]);
  const [showHint, setShowHint] = useState(false);
  const [unlockedCount, setUnlockedCount] = useState(0);
  const showCoaching = false;
  const [practiceRes, setPracticeRes] = useState<ISubmitAnswerResponse | null>(
    null
  );
  const [challengeRes, setChallengeRes] =
    useState<IChallengeAnswerResponse | null>(null);

  const [conceptMastery, setConceptMastery] = useState<
    IConceptMasteryResponse | undefined
  >(undefined);
  const { ContentCommonAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const { setMyPerformanceClickHandler } = useGlobalContext();
  const [performanceDrawer, setPerformanceDrawer] = useState(false);
  const toggleDrawer = (newOpen: boolean) => () => {
    setPerformanceDrawer(newOpen);
  };
  const myPerformanceClickHandler = () => {
    console.log('My Performance Clicked');
    toggleDrawer(true)();
    // alert('My Performance Clicked');
  };
  useEffect(() => {
    if (setMyPerformanceClickHandler) {
      setMyPerformanceClickHandler(() => myPerformanceClickHandler);
      return () => {
        setMyPerformanceClickHandler(null);
      };
    }
  }, [setMyPerformanceClickHandler]);
  useEffect(() => {
    setIsFirstQuestion(true);
  }, [palMode]);

  const resetQuestionStats = () => {
    setUserAttemptedAnswer([]);
    setDiagnosticResult(undefined);
    setOverallChallengeResult(undefined);
    setPracticeRes(null);
    setChallengeRes(null);
    setUnlockedCount(0);
    setSubmittedTime(0);
  };

  useEffect(() => {
    setQuestion(undefined);
    setQuestionMetadata(undefined);
    resetQuestionStats();
    if (!external_question_id) {
      getQuestionData();
    } else {
      fetchExternalQuestion();
      // getDiagnosticResult(
      //   '1581080b-a217-46bb-b031-fbc395341b41',
      //   '796766cb-8dd5-412d-a356-8346f5ec41bb'
      // );
    }
  }, [external_question_id, currentPalModeKey]);

  useEffect(() => {
    if (
      componentConfig.showTimer &&
      loading === 'completed' &&
      remainingTimeInSeconds <= 0
    ) {
      onSubmit(true);
    }
  }, [remainingTimeInSeconds, componentConfig]);

  const getDiagnosticResult = async (moduleId?: string, sessionId?: string) => {
    if (!moduleId || !sessionId) {
      return;
    }
    const res = await getDiagnosticResultsApi(sessionId, moduleId);
    const resGraph = await getKnowledgeConceptGraph({
      module_id: moduleId,
    });
    const knowledgeConcept = resGraph.data;
    const moduleMastery = (await getModuleMasteryApi({ module_id: moduleId }))
      ?.data;
    return {
      result: res.results,
      moduleMastery: moduleMastery,
      knowledgeConcept: knowledgeConcept,
    };
  };

  const getChallengeResult = async (
    moduleId?: string,
    sessionId?: string,
    conceptId?: string
  ) => {
    if (!moduleId || !sessionId || !conceptId) {
      return;
    }
    const res = await getChallengeResultsApi(sessionId, moduleId, conceptId);
    const resGraph = await getKnowledgeConceptGraph({
      module_id: moduleId,
    });
    const knowledgeConcept = resGraph.data;
    const moduleMastery = (await getModuleMasteryApi({ module_id: moduleId }))
      ?.data;
    return {
      result: res.results,
      moduleMastery: moduleMastery,
      knowledgeConcept: knowledgeConcept,
    };
  };

  const backButtonClick = useCallback(
    async (isHome?: true, isEndSession?: boolean) => {
      try {
        setLoading('loading');
        if (isHome || isDiagnostic || isEndSession) {
          if (external_session_id) {
            await endModuleSessionApi({ session_id: external_session_id });
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (isHome) {
          navigate(HOME, {
            replace: true,
          });
        } else {
          if (isDiagnostic || isEndSession) {
            navigate(`${PAL_KNOWLEDGE_CONCEPTS}/${external_module_id}`, {
              replace: true,
            });
          } else {
            let path = `${PAL_KNOWLEDGE_CONCEPTS}/${external_module_id}`;
            if (external_session_id) {
              path = path + '?sessionId=' + external_session_id;
            }
            navigate(path, {
              replace: true,
            });
          }
        }
      }
    },
    [external_module_id, external_session_id, navigate]
  );

  usePalBackNavigationHandlers(backButtonClick, [external_session_id]);

  const handleAnswerOverlayContinue = async (
    mode: 'practice' | 'challenge'
  ) => {
    if (!external_session_id || !external_module_id) {
      if (mode === 'practice') {
        setPracticeRes(null);
      } else {
        setChallengeRes(null);
      }
      return;
    }

    try {
      setLoading('loading-overlay-continue');
      let isNavigated = false;
      if (mode === 'practice') {
        await handleKnowledgeConceptsNavigation({
          sessionId: external_session_id,
          moduleId: external_module_id,
          navigateToKnowledgeGraph: true,
          decision: practiceRes?.navigation_decision as PalNavigationDection,
          nextConceptId: practiceRes?.next_concept_id,
          navigate,
          skipDiagnositc: true,
        });
        isNavigated = true;
      } else {
        const isComplete = challengeRes?.challenge_progress.is_complete;
        if (isComplete) {
          if (external_concept_id) {
            const challengeResult = await getChallengeResult(
              external_module_id,
              external_session_id,
              external_concept_id
            );
            setOverallChallengeResult(challengeResult);
          }
        } else {
          const nextQuestionId = challengeRes?.next_question?.question_id;
          navigate(
            PAL_CHALLENGE +
              '/' +
              external_module_id +
              '/' +
              external_session_id +
              '/' +
              external_concept_id +
              '/' +
              nextQuestionId,
            {
              replace: true,
            }
          );
          isNavigated = true;
        }
      }
      if (!isNavigated) {
        setLoading('completed');
      }
    } catch (err) {
      console.error(err);
      setLoading('error-overlay-continue');
    } finally {
      setPracticeRes(null);
      setChallengeRes(null);
    }
  };

  const getQuestionData = async () => {
    try {
      setLoading('loading');
      if (!external_session_id) {
        setLoading('error-session');
        return;
      }
      let isNavigated = false;
      if (isPractice) {
        const res = await getRecommendationsQuestionsAPI({
          session_id: external_session_id,
          count: 1,
        });
        const recommendations = res.data?.recommendations || [];
        const external_question_id = recommendations[0]?.question_id;
        if (!external_question_id) {
          setLoading('error');
          return;
        }
        const conceptId = res.data?.concept_id;
        navigate(
          PAL_PRACTICE +
            '/' +
            external_module_id +
            '/' +
            external_session_id +
            '/' +
            conceptId +
            '/' +
            external_question_id,
          {
            replace: true,
          }
        );
        isNavigated = true;
      } else if (isDiagnostic) {
        const res = await startDiagnosticApi(external_session_id, {});
        const external_question_id = res.data.first_question.question_id;
        if (!external_question_id) {
          setLoading('error');
          return;
        }
        const conceptId = res.data.first_question.primary_concept_id;
        navigate(
          PAL_DIAGNOSTIC +
            '/' +
            external_module_id +
            '/' +
            external_session_id +
            '/' +
            conceptId +
            '/' +
            external_question_id,
          {
            replace: true,
          }
        );
        isNavigated = true;
      } else if (isChallenge) {
        const res = await getChallengeApi(external_session_id);
        const external_question_id = res.next_question?.question_id;
        if (!external_question_id) {
          setLoading('error');
          return;
        }
        const conceptId = res.concept_id;
        navigate(
          PAL_CHALLENGE +
            '/' +
            external_module_id +
            '/' +
            external_session_id +
            '/' +
            conceptId +
            '/' +
            external_question_id,
          {
            replace: true,
          }
        );
        isNavigated = true;
      }
      if (!isNavigated) {
        setLoading('completed');
      }
    } catch (err) {
      setLoading('error');
    }
  };

  const updateStreak = (mode: 'practice' | 'challenge', isCorrect: boolean) => {
    if (!external_session_id || !external_concept_id) return;

    const getUpdatedStreak = () => {
      if (isCorrect) {
        return currentStreak >= 0 ? currentStreak + 1 : 1;
      } else {
        return currentStreak > 0 ? -1 : currentStreak - 1;
      }
    };

    dispatch(
      setStreak({
        session_id: external_session_id,
        concept_id: external_concept_id,
        [mode]: getUpdatedStreak(),
      })
    );
  };

  const updateQuestionCount = (mode: 'practice' | 'challenge' | 'warmup') => {
    if (!external_session_id || !external_concept_id) return;

    const getUpdatedQuestionCount = () => {
      return currentQuestionsAnswered + 1;
    };

    dispatch(
      setQuestionCount({
        session_id: external_session_id,
        concept_id: external_concept_id,
        [mode]: getUpdatedQuestionCount(),
      })
    );
  };

  const diagnosticCompleteAction = async () => {
    if (!external_session_id || !external_module_id) {
      return;
    }
    await handleKnowledgeConceptsNavigation({
      sessionId: external_session_id,
      moduleId: external_module_id,
      navigateToKnowledgeGraph: true,
      navigate,
      decision: 'force-navigate',
    });
  };

  const onSubmit = async (isTimeUp?: boolean) => {
    const questionId = question?.questionId;
    if (!external_session_id || !external_module_id || !questionId) {
      return;
    }

    try {
      setLoading('loading-submit');
      const timeValue = timer.value;
      setSubmittedTime(timeValue);
      if (isPractice) {
        const practiceRes = await submitAnswerApi(external_session_id, {
          question_id: questionId,
          answer: userAttemptedAnswer,
          time_taken_seconds: timeValue,
          answer_mode: AnswerModeEnum.ANSWERED,
          primary_concept_id: external_concept_id,
          hint_used:
            unlockedCount > 0
              ? (questionMetadata?.context?.hints?.[unlockedCount - 1]
                  ?.type as AnswerHintUsedEnum)
              : undefined,
        });
        updateStreak(
          'practice',
          practiceRes.submission_details.score?.value === 1
        );
        setPracticeRes(practiceRes);
      } else if (isDiagnostic) {
        const diagnosticRes = await submitDiagnosticAnswerApi(
          external_session_id,
          {
            question_id: questionId,
            answer: userAttemptedAnswer,
            time_taken_seconds: timeValue,
            answer_mode: AnswerModeEnum.ANSWERED,
            primary_concept_id: external_concept_id,
            hint_used: undefined,
          }
        );
        setUserAttemptedAnswer([]);
        const isComplete = diagnosticRes.is_complete;
        updateQuestionCount('warmup');
        if (isComplete) {
          await applyDiagnosticToPalApi(external_session_id, {});
          try {
            const diagnosticResult = await getDiagnosticResult(
              external_module_id,
              external_session_id
            );
            setDiagnosticResult(diagnosticResult);
          } catch (err) {
            diagnosticCompleteAction();
          }
        } else {
          const nextQuestionId = diagnosticRes.next_question?.question_id;
          const nextConceptId = diagnosticRes.next_question?.primary_concept_id;
          if (nextQuestionId) {
            navigate(
              PAL_DIAGNOSTIC +
                '/' +
                external_module_id +
                '/' +
                external_session_id +
                '/' +
                nextConceptId +
                '/' +
                nextQuestionId,
              {
                replace: true,
              }
            );
          } else {
            await handleKnowledgeConceptsNavigation({
              sessionId: external_session_id,
              moduleId: external_module_id,
              navigateToKnowledgeGraph: true,
              navigate,
            });
          }
        }
      } else if (isChallenge) {
        const challengeRes = await submitChallengeAnswerApi(
          external_session_id,
          {
            question_id: questionId,
            answer: isTimeUp ? [] : userAttemptedAnswer,
            time_taken_seconds: timeValue,
            answer_mode: isTimeUp
              ? AnswerModeEnum.TIMEOUT
              : AnswerModeEnum.ANSWERED,
            primary_concept_id: external_concept_id,
            hint_used:
              unlockedCount > 0
                ? (questionMetadata?.context?.hints?.[unlockedCount - 1]
                    ?.type as AnswerHintUsedEnum)
                : undefined,
          }
        );
        updateStreak(
          'challenge',
          challengeRes.submission_details.score?.value === 1
        );
        setChallengeRes(challengeRes);
      }
      timer.reset();
      setLoading('completed');
    } catch (err) {
      setLoading('error-submit');
      console.log(err);
    } finally {
      setIsFirstQuestion(false);
    }
  };

  const fetchQestionMetadata = async (quesId: string) => {
    try {
      const res = await fetchQuestionMetadataApi(quesId);
      setQuestionMetadata(res);
    } catch (err) {
      console.error(err);
      setQuestionMetadata(undefined);
    }
  };

  const fetchExternalQuestion = async () => {
    if (!external_question_id || !external_concept_id) {
      return;
    }
    try {
      setLoading('loading');
      const res = await getConceptMasteryApi({
        concept_id: external_concept_id,
        session_id: external_session_id,
      });
      setConceptMastery(res);
      timer.reset();
      const geneo_question_id = (
        await fetchInternalQuestionIdApi(external_question_id)
      )?.question_id;
      await fetchQestionMetadata(external_question_id);
      if (!geneo_question_id) {
        return;
      }
      const geneoQuestionRes =
        await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchQuestions(
          {
            questionIds: [geneo_question_id],
          }
        );
      setQuestion(geneoQuestionRes.data?.questions[0]);
      timer.start();
      setLoading('completed');
    } catch (err) {
      setLoading('error');
    }
  };

  const handleAnswerChange = useCallback(
    (answer: string) => {
      if (!question) return;
      const qType = question.questionType;
      if (qType === Question_QuestionEnum.QUESTION_TYPE_MCQM) {
        setUserAttemptedAnswer((prev) =>
          prev.includes(answer)
            ? prev.filter((a) => a !== answer)
            : [...prev, answer]
        );
      } else {
        setUserAttemptedAnswer([answer]);
      }
    },
    [question]
  );

  const renderConceptMasterBar = () => {
    return (
      <Box
        sx={{
          background: '#fff',
          borderRadius: '15px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
          padding: '16px 24px',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="elementSmallText" sx={{ color: '#64748b' }}>
            Your concept mastery
          </Typography>
          <Box sx={{ flex: 1 }}>
            <LinearProgress
              variant="determinate"
              value={conceptMastery?.concept_state?.mastery_percentage ?? 0}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: '#e5e7eb',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  bgcolor: '#6366f1',
                },
              }}
            />
          </Box>
          <Typography
            variant="elementSmallText"
            sx={{
              fontWeight: 700,
              color: '#6366f1',
            }}
          >
            {conceptMastery?.concept_state?.mastery_percentage ?? 0}%
          </Typography>
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography
            variant="elementSmallText"
            sx={{ color: '#64748b', textAlign: 'center' }}
          >
            ✨ Review these resources, then switch to{' '}
            <strong>Practice Mode</strong> to test your understanding
          </Typography>
        </Box>
      </Box>
    );
  };
  if (
    loading === 'loading' ||
    loading == 'loading-submit' ||
    loading == 'loading-overlay-continue' ||
    !isLoaderLoaded
  ) {
    const getHeadingSubHeading = () => {
      if (palMode == 'warmup') {
        // discuss
        return {
          heading: 'Understanding your learning path',
          subHeading: isFirstQuestion
            ? 'We’ll start by discovering your current level'
            : 'Adapting based on your recent answers',
        };
      }
      if (isFirstQuestion) {
        return {
          heading: `Let’s get started! 🚀`,
          subHeading: `We’ll begin with a question to understand your level`,
        };
      } else if (currentStreak >= 3) {
        return {
          heading: `You're on fire! 🔥`,
          subHeading: 'Time to take it up a notch with a tougher challenge',
        };
      } else if (currentStreak === 1 && !isFirstQuestion) {
        return {
          heading: `Nice comeback! 💪`,
          subHeading: `Let's build on that momentum with a similar one`,
        };
      } else if (currentStreak === -1) {
        return {
          heading: `Let's try a different angle 🎓`,
          subHeading: `Adjusting the difficulty so you can master this concept`,
        };
      } else if (currentStreak < -1) {
        return {
          heading: `Let's rebuild confidence 🌱`,
          subHeading: `Picking something that fits exactly where you are right now`,
        };
      } else {
        const optionsForCorrect = [
          {
            heading: `Let's level up! ⚡`,
            subHeading: `Picking something a bit more challenging for you`,
          },
          {
            heading: `Great work! 🎯`,
            subHeading: `Finding the perfect next question to grow your skills`,
          },
          {
            heading: `Keep the momentum! 🚀`,
            subHeading: `Tailoring the next question to push you further`,
          },
        ];
        return optionsForCorrect[
          Math.floor(Math.random() * optionsForCorrect.length)
        ];
      }
    };
    const getMapping = () => {
      switch (
        loading // discuss
      ) {
        case 'loading-overlay-continue':
          return {
            heading: 'Personalizing your next step',
            subHeading: 'Tailored to your performance',
            messages: ['PLEASE WAIT'],
          };
        case 'loading-submit': // discuss
          return {
            heading: 'Submitting your answer',
            subHeading: 'Checking your understanding',
            messages: ['PLEASE WAIT'],
          };
        case 'loading':
        default:
          return {
            ...getHeadingSubHeading(),
            messages: [
              'PAL IS STUDYING HOW YOU LEARN',
              'PAL IS PERSONALIZING THIS JUST FOR YOU',
              `PAL HAS HAND-PICKED YOUR ${
                isFirstQuestion ? 'FIRST' : 'NEXT'
              } QUESTION`,
            ],
          };
      }
    };
    const mapping = getMapping();
    return (
      <PalLoader
        contentLockKey={loading}
        emoji="🧠"
        heading={mapping.heading}
        subHeading={mapping.subHeading}
        messages={mapping.messages}
        isLoaderLoaded={isLoaderLoaded}
        setIsLoaderLoaded={setIsLoaderLoaded}
      />
      // <Box
      //   sx={{
      //     width: '100%',
      //     height: '100%',
      //     display: 'flex',
      //     alignItems: 'center',
      //     justifyContent: 'center',
      //     bgcolor: '#f8fafc',
      //     flexDirection: 'column',
      //     gap: 2,
      //   }}
      // >
      //   <Typography sx={{ fontSize: 48 }}>📚</Typography>
      //   <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1e293b' }}>
      //     {loading === 'loading'
      //       ? isFirstQuestion
      //         ? 'Curating questions just for you'
      //         : 'Adjusting the right difficulty for you'
      //       : loading === 'loading-submit'
      //       ? 'Submitting your answer...'
      //       : loading === 'loading-overlay-continue'
      //       ? 'Personalizing your next step'
      //       : ''}
      //   </Typography>
      // </Box>
    );
  }

  if (
    !question ||
    ['error', 'error-submit', 'error-overlay-continue'].includes(loading)
  ) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f8fafc',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography sx={{ fontSize: 48 }}>❓</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1e293b' }}>
          {loading === 'error'
            ? 'Could not load the question'
            : loading === 'error-submit'
            ? 'Error while submitting question'
            : loading === 'error-overlay-continue'
            ? 'Some error occured'
            : 'No question available'}
        </Typography>
      </Box>
    );
  }

  const metadataDifficulty = () => {
    const diff = questionMetadata?.difficulty?.toLocaleLowerCase();
    switch (diff) {
      case 'low':
        return 'Easy';
      case 'medium':
        return 'Medium';
      case 'high':
        return 'Hard';
      default:
        return undefined;
    }
  };

  const metaDataBloom = (
    learningObjectives?: IQuestionMetadataIdentifiedLearningObjective[]
  ) => {
    const bloomLevels = (learningObjectives || [])?.map(
      (val) => val.bloom_level
    );
    const bloomOrder = [
      'remember',
      'understand',
      'apply',
      'analyze',
      'evaluate',
      'create',
    ];
    const highestBloomLevel = bloomLevels.reduce((max, current) => {
      const currentIndex = bloomOrder.indexOf(current);
      const maxIndex = bloomOrder.indexOf(max);
      return currentIndex > maxIndex ? current : max;
    }, bloomOrder[0]);
    return highestBloomLevel
      ? toStringBloomLevel(highestBloomLevel)
      : undefined;
  };

  const marks = question.question?.model.value?.commonQuestionContent?.marks[0];
  const bloomLevel =
    metaDataBloom(questionMetadata?.identified_learning_objectives) ||
    toStringBloomLevel(question.questionMeta?.bloomLevel);
  const quesType = questionTypeTitleFromQuestionContent(question.question);
  const difficulty =
    metadataDifficulty() ||
    getDifficultyLevelString(question.questionMeta?.difficultyLevel) ||
    'Medium';

  const renderDifficultyDot = () => {
    const count = difficulty == 'Hard' ? 3 : difficulty == 'Medium' ? 2 : 1;
    return (
      <>
        {Array.from({ length: count }).map((_, idx) => (
          <Box
            key={idx}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: difficultyColors[difficulty || 'Medium'].dot,
            }}
          />
        ))}
      </>
    );
  };

  const renderQuestionHeader = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          background: 'linear-gradient(to right, #fafbfc, #f8f9fa)',
          border: '1px solid #eaeef2',
          padding: '6px 20px',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ flex: 1, gap: '15px' }}
        >
          <Typography
            sx={{ fontWeight: 800, fontSize: 16, color: '#1e293b', mr: 2 }}
          >
            Q.
          </Typography>
          {/* Question type tag - purple */}
          <Chip
            label={quesType}
            size="small"
            sx={{
              ml: 1,
              background: 'linear-gradient(135deg, #faf5ff, #f3e8ff)',
              border: '1px solid #a78bfa',
              color: '#7c3aed',
              fontSize: 11,
              fontWeight: 600,
              height: 22,
            }}
          />
          {/* Difficulty tag with dot */}
          {componentConfig.showDifficulty && (
            <Chip
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {renderDifficultyDot()}
                  {difficulty}
                </Box>
              }
              size="small"
              sx={{
                bgcolor: difficultyColors[difficulty || 'Medium'].bg,
                color: difficultyColors[difficulty || 'Medium'].color,
                fontSize: 11,
                fontWeight: 600,
                height: 22,
              }}
            />
          )}
          {renderTimer()}
        </Stack>
        {componentConfig.showHint &&
          (questionMetadata?.context?.hints || []).length > 0 && (
            <Button
              size="small"
              onClick={() => setShowHint(true)}
              sx={{
                textTransform: 'none',
                gap: 0.5,
                color: '#f59e0b',
                fontWeight: 600,
                fontSize: 13,
                paddingX: pxToRem(8),
                paddingY: pxToRem(5),
                border: '1px solid #f59e0b',
                background: 'white',
              }}
            >
              💡 Hint ({unlockedCount}/{questionMetadata?.context?.hints.length}
              )
            </Button>
          )}
        {componentConfig.showResources && (
          <IconButton
            // onClick={() => onSetShowResources(true)}
            title="Resources"
            sx={{ color: '#64748b' }}
          >
            <Typography sx={{ fontSize: 18 }}>📚</Typography>
          </IconButton>
        )}
        {componentConfig.showMarks && marks && (
          <Chip
            label={`${marks} Mark${marks ? (marks > 1 ? 's' : '') : ''}`}
            size="small"
            sx={{
              bgcolor: '#f8fafc',
              border: '1px solid #e5e7eb',
              fontWeight: 600,
              fontSize: 12,
            }}
          />
        )}
      </Box>
    );
  };

  const renderCoachingStrip = () => {
    return (
      showCoaching && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.75,
            px: 2.5,
            py: 2,
            background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
            borderBottom: '1px solid #fde68a',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              boxShadow: '0 2px 8px rgba(251,191,36,0.3)',
              flexShrink: 0,
            }}
          >
            🧑‍🏫
          </Box>
          <Typography
            sx={{
              fontSize: 14,
              lineHeight: 1.6,
              color: '#92400e',
              fontWeight: 500,
            }}
          >
            You&apos;ve done really well on this concept earlier — this should
            be easy for you 🙂
          </Typography>
        </Box>
      )
    );
  };

  const renderQuestionFooter = () => {
    return (
      <Box sx={{ mt: 'auto', pt: '12px' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            // gridTemplateColumns: 'auto 1fr auto',
            columnGap: '15px',
            rowGap: 1,
            alignItems: 'center',
            pt: '12px',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          {componentConfig.showBloomLevel && bloomLevel && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                Bloom Level:
              </Typography>
              <Chip
                label={`🧠 ${bloomLevel}`}
                size="small"
                sx={{
                  mt: 0.5,
                  bgcolor: '#f0f4ff',
                  color: '#6366f1',
                  fontWeight: 600,
                  fontSize: 11,
                }}
              />
            </Box>
          )}
          {/* Green submit button */}
          <Button
            onClick={() => onSubmit()}
            disabled={!userAttemptedAnswer || userAttemptedAnswer.length === 0}
            sx={{
              justifySelf: 'center',
              background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)',
              color: 'white',
              px: 5,
              py: 1.25,
              borderRadius: 2,
              fontWeight: 700,
              fontSize: 15,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              boxShadow: '0 4px 16px rgba(76,175,80,0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4caf50 0%, #43a047 100%)',
                boxShadow: '0 8px 24px rgba(76,175,80,0.4)',
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#9ca3af',
                boxShadow: 'none',
              },
            }}
          >
            SUBMIT
          </Button>
          {palMode !== 'warmup' && external_module_id && external_session_id ? (
            <Box sx={{ marginLeft: 'auto' }}>
              <EndSessionButton
                moduleId={external_module_id}
                sessionId={external_session_id}
                onEndSession={() => {
                  backButtonClick(undefined, true);
                }}
              />
            </Box>
          ) : (
            <Box />
          )}
        </Box>
        <PerformanceDrawer
          open={performanceDrawer}
          onClose={toggleDrawer(false)}
          module_id={external_module_id}
          session_id={external_session_id}
        />
      </Box>
    );
  };

  const renderRightPanel = () => {
    const renderPanel =
      componentConfig.showKnowledgeTree ||
      componentConfig.showLearnometer ||
      componentConfig.showLearnometerGain;
    if (!renderPanel) {
      return null;
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <Paper
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            p: 1.5,
            borderRadius: 2.5,
            bgcolor: 'white',
            borderLeft: '3px solid #667eea',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <Typography sx={{ fontSize: 16 }}>✨</Typography>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: '#374151',
              lineHeight: 1.45,
            }}
          >
            Stay focused — you&apos;re doing great! Every question is a step
            forward.
          </Typography>
        </Paper>
        {componentConfig.showLearnometer && (
          <Learnometer
            conceptName={conceptMastery?.concept_title}
            upiScore={conceptMastery?.concept_state?.upi_score}
            gainToday={
              componentConfig.showLearnometerGain
                ? conceptMastery?.concept_state.upi_score_gain
                : 0
            }
          />
        )}
        {componentConfig.showKnowledgeTree &&
          external_concept_id &&
          external_module_id && (
            <LearningPath
              conceptId={external_concept_id}
              moduleId={external_module_id}
            />
          )}
      </Box>
    );
  };

  const formatTime = (s: number) => {
    if (s < 0) {
      s = 0;
    }
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const renderTimer = () => {
    if (!componentConfig.showTimer) {
      return null;
    }
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ ml: 1, color: '#f59e0b' }}
      >
        <Typography sx={{ fontSize: 13 }}>⏱</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {formatTime(remainingTimeInSeconds)}
        </Typography>
      </Stack>
    );
  };

  const overlayMode: 'practice' | 'challenge' | null = practiceRes
    ? 'practice'
    : challengeRes
    ? 'challenge'
    : null;

  const submission_details =
    practiceRes?.submission_details || challengeRes?.submission_details;

  if (Boolean(overlayMode) && !overallChallengeRes) {
    return (
      <PalAnswerOverlay
        open={Boolean(overlayMode)}
        onContinue={() => {
          if (overlayMode) {
            updateQuestionCount(overlayMode);
            resetQuestionStats();
            handleAnswerOverlayContinue(overlayMode);
          }
        }}
        score={submission_details?.score?.value}
        streak={currentStreak}
        userSubmittedAnswer={userAttemptedAnswer}
        correctAnswer={submission_details?.correct_answer}
        question={question}
        accuracy={submission_details?.session_accuracy}
        xpGain={
          practiceRes?.concept_upi_score_updates.find(
            (val) => val.concept_id == external_concept_id
          )?.delta || 0
        }
        timeTakenInSeconds={submission_details?.time_taken_seconds}
        idealMaxTimeInSeconds={questionTimerInSeconds}
        palMode={palMode}
        isTimedOut={submission_details?.answer_mode == AnswerModeEnum.TIMEOUT}
        solution={questionMetadata?.solution || []}
        questioNumber={currentQuestionsAnswered + 1}
      />
    );
  }

  return (
    <>
      <PalModeChangePopup
        open={showPalModePopup}
        setOpen={onPalModePopupOpenChange}
        currentPalModeKey={currentPalModeKey}
      />
      <PerformancePopup
        results={diagnosticResult}
        open={!!diagnosticResult}
        onClose={() => {
          setDiagnosticResult(undefined);
          diagnosticCompleteAction();
        }}
        mode="warmup"
      />

      <PerformancePopup
        results={overallChallengeRes}
        open={!!overallChallengeRes}
        onClose={async () => {
          setOverallChallengeResult(undefined);
          try {
            setLoading('loading-overlay-continue');
            if (external_session_id && external_module_id) {
              await handleKnowledgeConceptsNavigation({
                sessionId: external_session_id,
                moduleId: external_module_id,
                navigateToKnowledgeGraph: true,
                navigate,
                skipDiagnositc: true,
              });
            }
            setLoading('completed');
          } catch (err) {
            console.error(err);
            setLoading('error-overlay-continue');
          }
        }}
        mode="challenge"
      />

      <PalAssessmentWrapper
        streak={componentConfig.showStreak ? currentStreak : undefined}
        conceptName={conceptMastery?.concept_title}
        rightPanel={renderRightPanel()}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          {renderQuestionHeader()}
          {renderCoachingStrip()}
          <Box
            sx={{
              background: '#fff',
              borderBottomLeftRadius: '15px',
              borderBottomRightRadius: '15px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              padding: '16px 24px',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
            }}
          >
            <QuestionContainer
              questionContent={question.question}
              isPalQuestion
              userAttemptedAnswer={userAttemptedAnswer}
              handleAnswerChange={handleAnswerChange}
              imageResizeEnabled
            />
            {renderQuestionFooter()}
          </Box>
        </Box>
        {/* {renderConceptMasterBar()} */}
      </PalAssessmentWrapper>
      <HintPopupPal
        open={showHint}
        handleClose={() => setShowHint(false)}
        hintData={questionMetadata?.context?.hints || []}
        unlockedCount={unlockedCount}
        setUnlockedCount={setUnlockedCount}
      />
    </>
  );
};

export default PalQuestion;

export function createUserAnswers(
  question: Question,
  userAttemptedAnswer: string[]
) {
  const questionValue = question.question?.model?.value;

  const result = isCorrectAnswer(question, userAttemptedAnswer);

  // ✅ MCQ SINGLE
  if (questionValue instanceof McqSingleContentModel) {
    return userAttemptedAnswer.map((ans) => ({
      text: ans,
      correct: result === QAttemptResultEnum.RESPONSE_CORRECT,
      feedback:
        result === QAttemptResultEnum.RESPONSE_CORRECT
          ? 'Correct answer.'
          : 'Incorrect. Review this concept.',
    }));
  }

  // ✅ MCQ MULTIPLE
  if (questionValue instanceof McqMultipleContentModel) {
    const correctAnswers = questionValue.correct;

    return userAttemptedAnswer.map((ans) => {
      const isCorrect = correctAnswers.includes(ans);

      return {
        text: ans,
        correct: isCorrect,
        partiallyCorrect:
          result === QAttemptResultEnum.RESPONSE_PARTIALLY_CORRECT,
        feedback: isCorrect ? 'Correct selection.' : 'Incorrect selection.',
      };
    });
  }

  // ✅ TRUE / FALSE
  if (questionValue instanceof TfContentModel) {
    const correct =
      questionValue.correct === TfContentModel_AnswerEnum.TF_ANSWER_T
        ? 'True'
        : 'False';

    const userAns = userAttemptedAnswer[0];

    return [
      {
        text: userAns,
        correct: userAns === correct,
        feedback:
          userAns === correct
            ? 'Correct answer.'
            : `Incorrect. Correct answer is ${correct}.`,
      },
    ];
  }

  // fallback
  return [];
}

export const EndSessionButton = ({
  moduleId,
  sessionId,
  onEndSession,
}: {
  moduleId: string;
  sessionId: string;
  onEndSession: () => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState<
    'completed' | 'loading-end-session' | 'error-end-session'
  >('completed');
  const [isLoaderLoaded, setIsLoaderLoaded] = useState(true);

  const [sessionRes, setSessionRes] = useState<ReviewResult | undefined>(
    undefined
  );

  const getCurrentSessionResult = async (
    moduleId?: string,
    sessionId?: string
  ) => {
    if (!moduleId || !sessionId) {
      return;
    }
    const res = await getMyPerformanceApi({
      module_id: moduleId,
      session_id: sessionId,
    });
    const resGraph = await getKnowledgeConceptGraph({
      module_id: moduleId,
    });
    const knowledgeConcept = resGraph.data;
    const moduleMastery = (await getModuleMasteryApi({ module_id: moduleId }))
      ?.data;
    return {
      result: res.results,
      moduleMastery: moduleMastery,
      knowledgeConcept: knowledgeConcept,
    };
  };

  if (loading == 'loading-end-session' || !isLoaderLoaded) {
    const getMapping = () => {
      switch (
        loading // discuss
      ) {
        case 'loading-end-session':
        default:
          return {
            heading: 'Fetching your current session details',
            subHeading: 'Tailored to your performance',
            messages: ['PLEASE WAIT'],
          };
      }
    };
    const mapping = getMapping();
    return (
      <PalLoader
        contentLockKey={loading}
        emoji="🧠"
        heading={mapping.heading}
        subHeading={mapping.subHeading}
        messages={mapping.messages}
        isLoaderLoaded={isLoaderLoaded}
        setIsLoaderLoaded={setIsLoaderLoaded}
      />
    );
  }

  return (
    <>
      <Button
        onClick={async () => {
          try {
            setLoading('loading-end-session');
            const sessionRes = await getCurrentSessionResult(
              moduleId,
              sessionId
            );
            setSessionRes(sessionRes);
            setLoading('completed');
          } catch (err) {
            console.error(err);
            setLoading('error-end-session');
          }
        }}
        sx={drawerStyles.endTestBtn}
      >
        {isMobile ? 'End' : 'End Session'}
      </Button>
      <PerformancePopup
        results={sessionRes}
        open={!!sessionRes}
        onEndSession={() => {
          onEndSession();
        }}
        onClose={() => {
          setSessionRes(undefined);
        }}
        mode="end-session"
      />
    </>
  );
};
